import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail, Send, ArrowLeft } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    setS(data);
  };
  useEffect(() => { load(); }, []);

  const setField = async (patch) => {
    setSaving(true);
    setS({ ...s, ...patch });
    try {
      await api.patch("/settings", patch);
    } catch (e) {
      toast.error("Could not save.");
      load();
    } finally {
      setSaving(false);
    }
  };

  const sendMyself = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/digest/send-me");
      if (data.ok) {
        toast.success("Sent to your inbox.");
      } else if (data.reason === "email_not_configured") {
        toast.info("Email delivery is being provisioned. Your preference is saved.");
      } else {
        toast.error(data.reason || "Could not send.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send.");
    } finally {
      setTesting(false);
    }
  };

  const sendEveryone = async () => {
    if (!window.confirm("Send the weekly digest now to everyone who opted in?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/digest/send-all");
      toast.success(`Sent ${data.sent} · failed ${data.failed}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send.");
    } finally {
      setSending(false);
    }
  };

  if (!s) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/app" data-testid="back-app" className="text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="inline w-4 h-4 mr-1" /> Back
      </Link>
      <h1 className="font-serif text-5xl mt-6 leading-tight">Settings</h1>

      <section className="mt-14 border border-border bg-card p-8">
        <div className="flex items-start gap-6">
          <Mail className="w-5 h-5 text-primary mt-1 shrink-0" />
          <div className="flex-1">
            <div className="font-serif text-2xl">Sunday Digest</div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              A gentle Sunday-morning email with this week's memory verse and closing prayer.
              We only send when you opt in — no other emails, no marketing.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <Switch checked={!!s.digest_opt_in} onCheckedChange={(v) => setField({ digest_opt_in: v })}
                data-testid="digest-toggle" disabled={saving} />
              <span className="text-sm">{s.digest_opt_in ? "You're subscribed" : "Off"}</span>
            </div>
            {s.digest_opt_in && (
              <Button variant="outline" size="sm" onClick={sendMyself} disabled={testing || !s.email_configured}
                data-testid="digest-send-me" className="rounded-full mt-5">
                <Send className="w-3.5 h-3.5 mr-2" /> {testing ? "Sending…" : "Send me a preview"}
              </Button>
            )}
            {!s.email_configured && (
              <p className="mt-4 text-xs text-muted-foreground italic">
                Email delivery is being provisioned. Your preference is saved — you'll start receiving digests once it's ready.
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="mt-8 border border-border bg-card p-8">
        <div className="font-serif text-2xl">Your Timothy</div>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          The one person you sense God is asking you to walk through the handbook with next.
        </p>
        <label className="block mt-5">
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Name</span>
          <input defaultValue={s.timothy_name || ""} data-testid="settings-timothy"
            onBlur={(e) => e.target.value !== s.timothy_name && setField({ timothy_name: e.target.value })}
            className="notebook-input w-full mt-2" placeholder="e.g. Sam" />
        </label>
      </section>

      {user?.is_leader && (
        <section className="mt-8 border border-border bg-card p-8">
          <div className="font-serif text-2xl">Leader tools</div>
          <p className="text-sm text-muted-foreground mt-2">
            Manually trigger the Sunday digest for everyone who opted in.
          </p>
          <Button onClick={sendEveryone} disabled={sending || !s.email_configured} data-testid="digest-send-all"
            className="rounded-full mt-5">
            <Send className="w-3.5 h-3.5 mr-2" /> {sending ? "Sending…" : "Send digest now"}
          </Button>
        </section>
      )}
    </div>
  );
}
